import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { canManageBillingWorkspace, canManageModuleToggles } from '../../lib/services/adminUsers'
import {
  createBillingPortalSession,
  createCheckoutSession,
  getBillingPortalSummary,
  getModuleBillingState,
  mutateModuleBilling,
  type BillingInterval,
  type BillingTier,
  type ModuleBillingKey,
  type ModuleBillingState,
} from '../../lib/services/billing'

const BILLING_TIERS: ReadonlyArray<{ tier: BillingTier; title: string; description: string }> = [
  { tier: 'standalone', title: 'Standalone', description: 'Single-facility ASC operators and DON-led teams.' },
  { tier: 'consultant', title: 'Consultant', description: 'Independent consultants managing multiple ASC clients.' },
  { tier: 'enterprise', title: 'Enterprise', description: 'Corporate or portfolio compliance teams.' },
]

const BILLING_SUMMARY_LOAD_ERROR = 'Billing information is temporarily unavailable. Please retry or contact support if this continues.'
const MODULE_BILLING_LOAD_ERROR = 'Module billing is temporarily unavailable. Please retry or contact support if this continues.'

type PendingModuleAction = {
  moduleKey: ModuleBillingKey
  enabled: boolean
}

function formatCurrency(cents: number | null, currency: string | null) {
  if (cents === null || !currency) return 'No preview available'
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100)
}

function formatDate(value: string | null): string {
  if (!value) return 'Not available'
  const parsed = new Date(value)
  return Number.isNaN(parsed.valueOf()) ? 'Not available' : parsed.toLocaleDateString()
}

export function BillingWorkspacePage() {
  const { viewerContext } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const canManage = canManageBillingWorkspace(viewerContext)
  const canManageToggles = canManageModuleToggles(viewerContext)
  const autoCheckoutHandledRef = useRef(false)
  const [selectedBillingInterval, setSelectedBillingInterval] = useState<BillingInterval | null>(null)
  const [billingError, setBillingError] = useState<string | null>(null)
  const [billingSuccess, setBillingSuccess] = useState<string | null>(null)
  const [portalError, setPortalError] = useState<string | null>(null)
  const [portalSuccess, setPortalSuccess] = useState<string | null>(null)
  const [moduleBillingError, setModuleBillingError] = useState<string | null>(null)
  const [moduleBillingSuccess, setModuleBillingSuccess] = useState<string | null>(null)
  const [pendingModuleAction, setPendingModuleAction] = useState<PendingModuleAction | null>(null)
  const [modulePreviews, setModulePreviews] = useState<Partial<Record<ModuleBillingKey, ModuleBillingState>>>({})
  const params = new URLSearchParams(location.search)
  const searchBillingState = params.get('billing')
  const searchRequestedTier = params.get('checkout_tier')
  const searchRequestedInterval = params.get('billing_interval')
  const locationState = (location.state as { billingFlash?: 'success' | 'cancelled' | 'trial-expired' | 'billing-required' } | null) ?? null
  const billingFlash = locationState?.billingFlash ?? null

  const summaryQuery = useQuery({
    queryKey: ['billing-workspace'],
    queryFn: getBillingPortalSummary,
    enabled: canManage,
  })

  const moduleBillingQuery = useQuery({
    queryKey: ['module-billing'],
    queryFn: getModuleBillingState,
    enabled: canManage,
  })

  const checkoutMutation = useMutation({
    mutationFn: async (payload: { tier: BillingTier; billing_interval: BillingInterval }) => await createCheckoutSession(payload),
  })

  const portalMutation = useMutation({
    mutationFn: async () => await createBillingPortalSession({ return_path: '/billing' }),
  })

  const moduleBillingMutation = useMutation({
    mutationFn: async (payload: { moduleKey: ModuleBillingKey; enabled: boolean; previewOnly: boolean }) =>
      await mutateModuleBilling(payload.moduleKey, payload.enabled, payload.previewOnly),
    onSuccess: async (response) => {
      setModulePreviews((current) => ({
        ...current,
        [response.module.module_key]: response.module,
      }))
      await moduleBillingQuery.refetch()
    },
  })

  const billingInterval =
    (searchRequestedInterval === 'monthly' || searchRequestedInterval === 'annual'
      ? searchRequestedInterval
      : selectedBillingInterval ?? summaryQuery.data?.billing_interval ?? 'monthly') as BillingInterval

  useEffect(() => {
    if (searchBillingState === 'trial-expired') {
      navigate(location.pathname, { replace: true, state: { billingFlash: 'trial-expired' } })
      return
    }
    if (searchBillingState === 'billing-required') {
      navigate(location.pathname, { replace: true, state: { billingFlash: 'billing-required' } })
      return
    }
    if (searchBillingState === 'stripe-success') {
      navigate(location.pathname, { replace: true, state: { billingFlash: 'success' } })
      void Promise.all([summaryQuery.refetch(), moduleBillingQuery.refetch()])
      return
    }
    if (searchBillingState === 'stripe-cancelled') {
      navigate(location.pathname, { replace: true, state: { billingFlash: 'cancelled' } })
      return
    }
    if (!searchRequestedTier || !canManage || autoCheckoutHandledRef.current || checkoutMutation.isPending) return
    if (!BILLING_TIERS.some((entry) => entry.tier === searchRequestedTier)) return
    autoCheckoutHandledRef.current = true
    navigate(location.pathname, { replace: true })
    void checkoutMutation
      .mutateAsync({
        tier: searchRequestedTier as BillingTier,
        billing_interval: searchRequestedInterval === 'annual' ? 'annual' : 'monthly',
      })
      .then((session) => {
        window.location.assign(session.url)
      })
      .catch((error: Error) => {
        setBillingSuccess(null)
        setBillingError(error.message)
      })
  }, [canManage, checkoutMutation, location.pathname, navigate, moduleBillingQuery, searchBillingState, searchRequestedInterval, searchRequestedTier, summaryQuery])

  async function launchStripeCheckout(tier: BillingTier) {
    setBillingError(null)
    setBillingSuccess(`Redirecting to Stripe checkout for the ${tier} plan...`)
    try {
      const session = await checkoutMutation.mutateAsync({
        tier,
        billing_interval: billingInterval,
      })
      window.location.assign(session.url)
    } catch (error) {
      setBillingSuccess(null)
      setBillingError((error as Error).message)
    }
  }

  async function launchBillingPortal() {
    setPortalError(null)
    setPortalSuccess('Opening Stripe billing portal...')
    try {
      const session = await portalMutation.mutateAsync()
      window.location.assign(session.url)
    } catch (error) {
      setPortalSuccess(null)
      setPortalError((error as Error).message)
    }
  }

  if (!canManage) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        Billing management is restricted to tenant admins and superadmins.
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Billing Workspace</h1>
        <p className="mt-1 text-sm text-slate-600">
          Manage subscription tier checkout, open the authenticated Stripe billing portal, and review module billing state.
        </p>
      </header>

      <section className="v11-section p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Subscription Overview</h2>
            <p className="mt-1 text-sm text-slate-600">Current Stripe-backed tenant commercial state.</p>
          </div>
          <button
            className="btn-primary text-sm md:w-auto"
            disabled={portalMutation.isPending || !summaryQuery.data?.stripe_customer_configured}
            onClick={() => void launchBillingPortal()}
          >
            {portalMutation.isPending ? 'Opening Portal...' : 'Open Billing Portal'}
          </button>
        </div>

        {summaryQuery.isLoading && <p className="mt-3 text-sm text-slate-500">Loading billing workspace...</p>}
        {summaryQuery.error && <p className="mt-3 text-sm text-red-600">{BILLING_SUMMARY_LOAD_ERROR}</p>}
        {summaryQuery.data && (
          <>
            <dl className="mt-4 grid gap-3 md:grid-cols-3">
              <div className="v11-mini-metric text-left">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Organization</dt>
                <dd className="mt-1 text-sm font-semibold text-slate-900">{summaryQuery.data.organization_name}</dd>
              </div>
              <div className="v11-mini-metric text-left">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Current Tier</dt>
                <dd className="mt-1 text-sm font-semibold capitalize text-slate-900">{summaryQuery.data.current_tier}</dd>
              </div>
              <div className="v11-mini-metric text-left">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Subscription Status</dt>
                <dd className="mt-1 text-sm font-semibold capitalize text-slate-900">{summaryQuery.data.subscription_status}</dd>
              </div>
              <div className="v11-mini-metric text-left">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Billing Interval</dt>
                <dd className="mt-1 text-sm font-semibold capitalize text-slate-900">
                  {summaryQuery.data.billing_interval ?? 'Not started'}
                </dd>
              </div>
              <div className="v11-mini-metric text-left">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Trial Ends</dt>
                <dd className="mt-1 text-sm font-semibold text-slate-900">{formatDate(summaryQuery.data.trial_ends_at)}</dd>
              </div>
              <div className="v11-mini-metric text-left">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Current Period End</dt>
                <dd className="mt-1 text-sm font-semibold text-slate-900">{formatDate(summaryQuery.data.current_period_end)}</dd>
              </div>
            </dl>
            {!summaryQuery.data.stripe_customer_configured && (
              <p className="mt-3 text-sm text-amber-700">
                Stripe billing has not been initialized for this tenant yet. Start checkout first to create the customer profile.
              </p>
            )}
          </>
        )}

        {portalError && <p className="mt-3 text-sm text-red-600">{portalError}</p>}
        {portalSuccess && <p className="mt-3 text-sm text-emerald-700">{portalSuccess}</p>}
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Plan Checkout</h2>
        <p className="mt-1 text-sm text-slate-600">
          Launch tenant-scoped Stripe checkout for a base plan. After checkout completes, this workspace becomes the single return target.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <label className="text-sm font-semibold text-slate-700" htmlFor="billing-interval">
            Billing interval
          </label>
          <select
            id="billing-interval"
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            value={billingInterval}
            onChange={(event) => setSelectedBillingInterval(event.target.value as BillingInterval)}
          >
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {BILLING_TIERS.map((plan) => (
            <div key={plan.tier} className="v11-dashboard-card p-4">
              <p className="font-brand text-lg font-bold text-[var(--brand-deep)]">{plan.title}</p>
              <p className="mt-2 text-sm text-slate-600">{plan.description}</p>
              <button
                className="btn-primary mt-4 w-full text-sm"
                disabled={checkoutMutation.isPending}
                onClick={() => void launchStripeCheckout(plan.tier)}
              >
                {checkoutMutation.isPending ? 'Redirecting...' : `Start ${plan.title} Checkout`}
              </button>
            </div>
          ))}
        </div>
        {billingFlash === 'cancelled' && <p className="mt-3 text-sm text-red-600">Stripe checkout was cancelled before completion.</p>}
        {billingFlash === 'trial-expired' && (
          <p className="mt-3 text-sm text-amber-700">
            Your free trial has expired. Complete billing to restore full workspace access.
          </p>
        )}
        {billingFlash === 'billing-required' && (
          <p className="mt-3 text-sm text-amber-700">
            Billing attention is required before normal workspace access can continue.
          </p>
        )}
        {billingError && <p className="mt-3 text-sm text-red-600">{billingError}</p>}
        {billingSuccess && <p className="mt-3 text-sm text-emerald-700">{billingSuccess}</p>}
        {billingFlash === 'success' && (
          <p className="mt-3 text-sm text-emerald-700">
            Stripe checkout completed. Billing status will refresh as webhook updates arrive.
          </p>
        )}
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Module Billing</h2>
        <p className="mt-1 text-sm text-slate-600">
          Review tenant-wide module billing and preview or apply Stripe subscription-item changes.
        </p>
        {!canManageToggles && (
          <p className="mt-3 text-sm text-amber-700">
            Module apply controls remain restricted to backend superadmins. Current module state is still visible here for tenant billing review.
          </p>
        )}
        {moduleBillingQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading module billing...</p>}
        {moduleBillingQuery.error && <p className="mt-2 text-sm text-red-600">{MODULE_BILLING_LOAD_ERROR}</p>}
        {moduleBillingQuery.data && (
          <>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <span>Subscription status: {moduleBillingQuery.data.subscription_status}</span>
              <span>Interval: {moduleBillingQuery.data.billing_interval}</span>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {moduleBillingQuery.data.modules.map((module) => {
                const preview = modulePreviews[module.module_key]?.preview ?? module.preview
                const hasActiveEntitlement = module.has_active_subscription_item || module.entitlement_status === 'active'
                const nextEnabled = !hasActiveEntitlement
                const pendingActionMatches =
                  pendingModuleAction?.moduleKey === module.module_key && pendingModuleAction?.enabled === nextEnabled
                return (
                  <div key={module.module_key} className="v11-dashboard-card p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-brand text-lg font-bold text-[var(--brand-deep)]">{module.title}</p>
                        <p className="mt-1 text-sm text-slate-600">{module.description}</p>
                      </div>
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                        {module.entitlement_status}
                      </span>
                    </div>
                    <dl className="mt-3 grid grid-cols-2 gap-2 text-sm text-slate-600">
                      <div>
                        <dt className="font-semibold text-slate-800">Facilities</dt>
                        <dd>{module.facility_count}</dd>
                      </div>
                      <div>
                        <dt className="font-semibold text-slate-800">Billed Qty</dt>
                        <dd>{module.billed_quantity}</dd>
                      </div>
                      <div>
                        <dt className="font-semibold text-slate-800">Enabled Facilities</dt>
                        <dd>{module.enabled_facility_count}</dd>
                      </div>
                      <div>
                        <dt className="font-semibold text-slate-800">Next Action</dt>
                        <dd>{nextEnabled ? 'Enable add-on' : 'Remove add-on'}</dd>
                      </div>
                    </dl>
                    <p className="mt-3 text-sm text-slate-600">
                      Preview amount:{' '}
                      <span className="font-semibold text-slate-800">
                        {formatCurrency(preview?.total_delta_cents ?? null, preview?.currency ?? null)}
                      </span>
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700"
                        disabled={moduleBillingMutation.isPending || !canManageToggles}
                        onClick={async () => {
                          setModuleBillingError(null)
                          setModuleBillingSuccess(null)
                          setPendingModuleAction({
                            moduleKey: module.module_key,
                            enabled: nextEnabled,
                          })
                          try {
                            await moduleBillingMutation.mutateAsync({
                              moduleKey: module.module_key,
                              enabled: nextEnabled,
                              previewOnly: true,
                            })
                            setModuleBillingSuccess(`Preview ready for ${module.title}. Review the amount before applying.`)
                          } catch (error) {
                            setModuleBillingError((error as Error).message)
                          }
                        }}
                      >
                        Preview {nextEnabled ? 'Enable' : 'Removal'}
                      </button>
                      <button
                        className="btn-primary text-sm"
                        disabled={moduleBillingMutation.isPending || !pendingActionMatches || !canManageToggles}
                        onClick={async () => {
                          setModuleBillingError(null)
                          setModuleBillingSuccess(null)
                          try {
                            await moduleBillingMutation.mutateAsync({
                              moduleKey: module.module_key,
                              enabled: nextEnabled,
                              previewOnly: false,
                            })
                            setModuleBillingSuccess(`${module.title} billing updated and feature entitlements synchronized.`)
                            setPendingModuleAction(null)
                          } catch (error) {
                            setModuleBillingError((error as Error).message)
                          }
                        }}
                      >
                        {moduleBillingMutation.isPending && pendingActionMatches
                          ? 'Applying...'
                          : nextEnabled
                            ? 'Apply Enable'
                            : 'Apply Removal'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
        {moduleBillingError && <p className="mt-3 text-sm text-red-600">{moduleBillingError}</p>}
        {moduleBillingSuccess && <p className="mt-3 text-sm text-emerald-700">{moduleBillingSuccess}</p>}
      </section>
    </div>
  )
}
